import type { Href } from 'expo-router';

import { isIosWeb } from '@/lib/isIosWeb';
import type { User } from '@/types/user';

/**
 * Every campaign route, defined once. Screens compare the server's answer
 * against these instead of re-typing the strings — a route literal drifting
 * out of sync with this switch is how a screen ends up redirecting to itself.
 */
export const GIFTING_ROUTES = {
  landing: '/season-of-gifting',
  start: '/season-of-gifting/start',
  complete: '/season-of-gifting/complete',
  choose: '/season-of-gifting/choose',
  secured: '/season-of-gifting/secured',
  getTheApp: '/season-of-gifting/get-the-app',
  // Not a server step — reached only by explicit navigation from the
  // completion form's email_taken branch.
  linkAccount: '/season-of-gifting/link-account',
} as const;

/**
 * Where the campaign funnel goes next. The decision itself is the SERVER's
 * (`user.gifting_next_step`); this only maps its answer onto a route, so the
 * landing, the campaign index, and the completion screen can't disagree about
 * what "next" means.
 *
 * `null` means "no funnel route — stay on the landing". The campaign index
 * renders <GiftingWebLanding /> whenever this returns null, which is what
 * finally makes its "for college athletes" empty state reachable: authed users
 * were always redirected away before that component could mount.
 *
 * Returning a route for an ineligible user is what caused the redirect loop —
 * index sent them to /choose, /choose sent them back to index, forever.
 *
 * The `?? complete` fallback covers a payload from before the field existed —
 * that screen self-redirects if there's nothing to collect, so an old client
 * lands correctly either way.
 */
export function giftingRouteFor(user: User | null): Href | null {
  if (!user) return GIFTING_ROUTES.start;

  // ABSENT is not the same as UNKNOWN. Absent = a server from before the
  // field existed → the completion screen is the safe legacy target. An
  // unrecognized VALUE = a server newer than this build → fail closed to the
  // landing, because the one thing we know about a step we don't understand
  // is that none of our screens is the right one. Failing open here walks
  // users into a form whose endpoint may refuse them (cross-model review
  // finding), and installed clients can't be patched atomically.
  const step = user.gifting_next_step;
  if (step == null) return GIFTING_ROUTES.complete;

  switch (step) {
    case 'secured':
      return GIFTING_ROUTES.secured;
    case 'choose':
      // Web signs up, the APP claims (Jason's call, 8/6): a ready-to-choose
      // athlete on an iPhone browser is handed to the App Store instead of
      // the web grid. The choosing screens all compare against
      // GIFTING_ROUTES.choose, so this ONE branch also walls them off on iOS
      // web — deep links included. Android/desktop keep the full web claim;
      // there is no Android build to hand them to.
      return isIosWeb() ? GIFTING_ROUTES.getTheApp : GIFTING_ROUTES.choose;
    case 'complete':
      return GIFTING_ROUTES.complete;
    case 'ineligible':
      return null;
    default:
      return null;
  }
}
